import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { company, products } from "../mock/mock";
import { WhatsAppIcon } from "../components/WhatsAppFloat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill in the required fields", description: "Name, email and message are required." });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form, { withCredentials: true });
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
      toast({ title: "Message sent!", description: "Thanks for reaching out. Our team will get back to you shortly." });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Visit Us", value: company.address },
    { icon: Phone, label: "Call Us", value: company.phone, href: `tel:${company.phoneHref}` },
    { icon: WhatsAppIcon, label: "WhatsApp", value: company.whatsapp, href: `https://wa.me/${company.whatsappHref}`, external: true, tone: "green" },
    { icon: Mail, label: "Email Us", value: company.email, href: `mailto:${company.email}` },
    { icon: Clock, label: "Working Hours", value: company.hours },
  ];

  return (
    <div className="bg-white">
      <section className="bg-[#141414] py-16">
        <div className="max-w-[1140px] mx-auto px-5">
          <p className="text-[#D80000] font-heading font-semibold tracking-widest text-sm">CONTACT</p>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3">Let&apos;s Talk Now</h1>
          <p className="text-white/60 mt-4 max-w-2xl">Tell us what you need made and we&apos;ll get you a quote.</p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-[1140px] mx-auto px-5 grid grid-cols-1 lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            {info.map((it) => (
              <div key={it.label} className="flex items-start gap-4">
                <span className={`inline-flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${it.tone === "green" ? "bg-[#25D366]/10" : "bg-[#D80000]/10"}`}>
                  <it.icon className={`w-5 h-5 ${it.tone === "green" ? "text-[#25D366]" : "text-[#D80000]"}`} />
                </span>
                <div>
                  <p className="font-heading font-semibold text-[#1a1a1a]">{it.label}</p>
                  {it.href ? (
                    <a
                      href={it.href}
                      target={it.external ? "_blank" : undefined}
                      rel={it.external ? "noopener noreferrer" : undefined}
                      className={`text-[#1a1a1a]/70 ${it.tone === "green" ? "hover:text-[#25D366]" : "hover:text-[#D80000]"}`}
                    >
                      {it.value}
                    </a>
                  ) : (
                    <p className="text-[#1a1a1a]/70">{it.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-lg border border-gray-100 shadow-lg p-7 md:p-9">
            <h2 className="font-heading text-2xl font-semibold text-[#1a1a1a] mb-6">Request a Quote</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Your name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="Phone number" />
                </div>
                <div className="space-y-2">
                  <Label>Product of Interest</Label>
                  <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                    <SelectTrigger><SelectValue placeholder="Select a product" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.slug} value={p.slug}>{p.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message *</Label>
                <Textarea id="message" rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your project, quantities and timeline..." />
              </div>
              <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#D80000] text-white font-semibold px-7 py-3 rounded-md hover:bg-[#b00000] disabled:opacity-70">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
